import * as React from "react"
import { Button, Typography, TextField } from '@mui/material';
import Dialog from 'components/Dialog';
import Prompt from 'components/Prompt';
import { useSelector, useDispatch } from "react-redux";
import { setOpen } from './slice';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import zparse from 'utils/zparse';

import { ReactComponent as Reset } from 'images/icons/reset.svg';
import seemore from 'images/seemore_title.svg';
import seemore_thinking from 'images/seemore_thinking.svg';

const Chat = (props) => {
	const initialHistory = React.useMemo(() => [
		{assistant: '<p>Hi! I’m Seemore, your personal Travel Guide. Ask me anything you want about visiting South Walton.</p>'},
	], []);
	const { executeRecaptcha } = useGoogleReCaptcha();
	const [question, setQuestion] = React.useState('');
	const [history, setHistory] = React.useState(initialHistory);
	const [thinking, setThinking] = React.useState(false);
	const [mailSuccess, setMailSuccess] = React.useState(null);
	const [email, setEmail] = React.useState('');
	const promptRef = React.useRef();
	const successPromptRef = React.useRef();
	const dispatch = useDispatch();
	const isOpen = useSelector((state) => state.chat?.open);
	const ref = React.useRef();
	const logRef = React.useRef();

	const resetChat = () => {
		setHistory(initialHistory);
		setThinking(false);
		setEmail('');
	}

	const titleButtons = [
		<Button key="resetChat" onClick={resetChat} disableRipple aria-label="Reset chat" variant="icon"><Reset /></Button>
	]

	const seemoreStyle = {
		position: 'absolute',
		// clipPath: 'inset(0 0 2px 0)',
	}

	const seemoreTitle = {
		marginLeft: '147px',
	}

	const Title = <>
		<img src={seemore} style={seemoreStyle} alt="" />
		<div style={seemoreTitle}>Ask Seemore</div>
	</>

	const slotProps = {
		htmlInput: {
			onKeyUp: (evt) => {
				if (evt.key === 'Enter' && ref.current)sendMessage();
			}
		}
	}

	const sendMessage = React.useCallback((evt) => {
		const query = ref.current?.value || question; // Try to get the field value first, assuming the REF is valid because the state won't have the correct value if native autocomplete was used
		setQuestion('');
		if (query === '') {
			setHistory([
				...history,
				{assistant: '<p>Go ahead and ask me anything by writing a message in the field below and hitting the SEND MEESSAGE button.</p>'},
			]);
			return;
		}
		if (!executeRecaptcha) {
			setHistory([
				...history,
				{assistant: '<p>Sorry, but I couldn\'t complete your request at this time.</p>'},
			]);
			return null;
		}
		setHistory([
			...history,
			{user: `<p>${query}</p>`},
		]);
		setThinking(true);
		executeRecaptcha('chat')
			.then(async token => {
				const prompt = {
					query,
					format: 'html',
					// output: 'sse',
					recaptcha: token,
					history: history.slice(0, -1),
				}
				const options = {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(prompt),
				}
				fetch(`${process.env.GATSBY_ZMS_API_URL}chat/question/`, options)
					.then((response) => response.json())
					.then((json) => {
						setThinking(false);
						if (json.data.length > 0) {
							setHistory((state) => {
								let current;
								if (state[state.length-1].assistant !== undefined) {
									current = state.pop();
								} else {
									current = {
										'assistant': '',
									}
								};
								current.assistant += json.data[0].contentHTML;
								return [
									...state,
									current,
								];
							});
							if (window !== 'undefined') {
								window.dataLayer = window.dataLayer||[];
								window.dataLayer.push({
									event: 'chat_submit',
									formID: 'chatbot',
									success: true,
									query: query,
									response: history[history.length-1].assistant,
								});
							}
						} else {
							setHistory((state) => {
								let current;
								if (state[state.length-1].assistant !== undefined) {
									current = state.pop();
								} else {
									current = {
										'assistant': '',
									}
								};
								current.assistant += `<p>Sorry I wasn't able to continue the conversation at this time. Please try again later.</p>`;
								return [
									...state,
									current,
								];
							});
							if (typeof window !== 'undefined') {
								window.dataLayer = window.dataLayer||[];
								window.dataLayer.push({
									event: 'chat_error',
									formID: 'chatbot',
									history: history,
								});
							}
						}
					})
					.catch(function (err) {
						setThinking(false);
						setHistory((state) => {
							let current;
							if (state[state.length-1].assistant !== undefined) {
								current = state.pop();
							} else {
								current = {
									'assistant': '',
								}
							};
							current.assistant += `<p>Sorry I wasn't able to continue the conversation at this time. Please try again later.</p>`;
							return [
								...state,
								current,
							];
						});
						if (typeof window !== 'undefined') {
							window.dataLayer = window.dataLayer||[];
							window.dataLayer.push({
								event: 'chat_error',
								formID: 'chatbot',
								history: history,
							});
						}
					})
				;
			})
			.catch(err => {
				setThinking(false);
				setHistory([
					...history,
					{assistant: '<p>Sorry, but I couldn\'t complete your request at this time.</p>'},
				]);
				console.log(err);
				if (typeof window !== 'undefined') {
					window.dataLayer = window.dataLayer||[];
					window.dataLayer.push({
						event: 'chat_error',
						formID: 'chatbot',
						error: err,
						history: history,
					});
				}
			})
		;
	}, [executeRecaptcha, history, setHistory, question]);

	const handleSave = () => {
		if (!!promptRef?.current === false)return;
		promptRef.current.open();
	}

	const Actions = <>
		<TextField inputRef={ref} className="Chat-input" fullWidth={true} type="text" placeholder="Write a message..." name="question" value={question} slotProps={slotProps} onChange={(evt) => setQuestion(evt.target.value)} />
		<div>
			<Button onClick={sendMessage} variant="dialog-dark">Send Message</Button>
			<Button onClick={handleSave} variant="underlined">Save Chat</Button>
		</div>
	</>

	const handleClose = (evt, reason) => {
		dispatch(setOpen(false));
	}

	const sendEmail = React.useCallback(() => {
		if (!executeRecaptcha) {
			console.log('Recaptch failed');
			return null;
		}
		executeRecaptcha('saveEmail')
			.then(async token => {
				const data = {
					to: email,
					recaptcha: token,
					history: history,
				}
				const options = {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(data),
				}
				const response = await fetch(`${process.env.GATSBY_ZMS_API_URL}chat/mail/`, options);
				setMailSuccess(response.status === 200);
				promptRef.current?.close();
				successPromptRef.current?.open();
			})
			.catch(err => {
				setMailSuccess(false);
				if (typeof window !== 'undefined') {
					window.dataLayer = window.dataLayer||[];
					window.dataLayer.push({
						event: 'chat_error',
						formID: 'chatbot',
						error: err,
						history: history,
					});
				}
			})
		;
	}, [executeRecaptcha, history, email]);
	
	React.useEffect(() => {
		if (!!logRef.current === false)return;
		const histories = logRef.current.querySelectorAll('.Chat-entry');
		if (histories.length === 0)return;
		const last = histories[histories.length-1];
		last.scrollIntoView({
			behavior: 'smooth',
		});
	}, [history, logRef]);

	return (
		<>
		<Dialog open={isOpen} onClose={handleClose} title={Title} titleButtons={titleButtons} actions={Actions} className="Chat-root">
			<Typography component="div" variant="h6" className="uppercase">Ask Seemore Disclaimer</Typography>
			<p>Please refrain from sharing any sensitive personal information in your messages. To save your conversation, hit the “Save Convo” button at the bottom of the chat and type in your email. We’ll send you a copy of the entire chat along with the links provided.</p>
			<div className="Chat-content">
				<div ref={logRef} className="Chat-log" role="log" aria-live="polite">
					{ [...history].map((message, index) => <div key={index} className={`Chat-entry ${Object.keys(message).join('')}`}><span>{zparse(Object.values(message).join(''))}</span></div>)}
					{ thinking && <div className="Chat-loader"><img src={seemore_thinking} alt="Thinking"/></div> }
				</div>
			</div>
		</Dialog>
		<Prompt ref={promptRef} title={'Save Chat?'} mailSuccess={mailSuccess}>
			<div className="SaveChat-content">
				<Typography variant="body1">If you want to save your convo, fill out your email & we’ll send you a copy with all of the links provided to your inbox.</Typography>
				{ mailSuccess === false && <Typography variant="body1">There was a problem trying to send the email.</Typography> }
				<TextField fullWidth={true} type="text" label="Email" name="email" value={email} onChange={(evt) => setEmail(evt.target.value)} />
				<Button onClick={() => sendEmail()} variant="outlined">Email Me a Copy</Button>
			</div>
		</Prompt>

		<Prompt ref={successPromptRef} title={'Chat Sent Successfully'}>
			<div className="SaveChat-content SaveChat-content-success">
				<Typography variant="body1">Your conversation with Seemore has been email to <strong>{email}</strong>.<br/>Check your inbox for a copy of your chat log and all shared links.</Typography>
				<div>
					<Button onClick={() => successPromptRef.current.close()} variant="outlined">Close</Button>
					<Button onClick={() => {successPromptRef.current.close(); resetChat()}} variant="underlined">Start New Chat</Button>
				</div>
				<Typography variant="body2">Didn't receive the email? <Button onClick={sendEmail}>Resend it.</Button></Typography>
			</div>
		</Prompt>
		</>
	)
}

export default Chat;